import {
  CommonListModel,
  PwmProfileStrategyItemCreateDocument,
  PwmProfileStrategyItemDeleteDocument,
  PwmProfileStrategyItemGetByIdDocument,
  PwmProfileStrategyItemListDocument,
  PwmProfileStrategyItemListPaginatedDocument,
  PwmProfileStrategyItemUpdateDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { PartialNullable } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IPwmProfileStrategyItemCreateVars,
  IPwmProfileStrategyItemModel,
  IPwmProfileStrategyItemPaginatedResponse,
  IPwmProfileStrategyItemPaginatedVars,
  IPwmProfileStrategyItemUpdateVars,
} from "./types";

export async function getPwmProfileStrategyItemPaginated(
  variables: IPwmProfileStrategyItemPaginatedVars
): Promise<IPwmProfileStrategyItemPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileStrategyItemListPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileStrategyItemListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createPwmProfileStrategyItem(
  variables: IPwmProfileStrategyItemCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileStrategyItemCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict own lists
    executeEviction(client, {
      typename: "PwmProfileStrategyItem",
      fieldNames: [
        "pwmProfileStrategyItemListPaginated",
        "pwmProfileStrategyItemList",
      ],
    });

    // Evict all parent PwmProfileStrategy entities (they reference these items)
    executeEviction(client, {
      typename: "PwmProfileStrategy",
      fieldNames: [
        "pwmProfileStrategyListPaginated",
        "pwmProfileStrategyGetById",
      ],
    });

    return res?.data?.pwmProfileStrategyItemCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updatePwmProfileStrategyItem(
  variables: IPwmProfileStrategyItemUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileStrategyItemUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + own lists + getById
    executeEviction(client, {
      typename: "PwmProfileStrategyItem",
      id: variables.id.toString(),
      fieldNames: [
        "pwmProfileStrategyItemListPaginated",
        "pwmProfileStrategyItemList",
        "pwmProfileStrategyItemGetById",
      ],
    });

    // Evict all parent PwmProfileStrategy entities (they reference these items)
    executeEviction(client, {
      typename: "PwmProfileStrategy",
      fieldNames: [
        "pwmProfileStrategyListPaginated",
        "pwmProfileStrategyGetById",
      ],
    });

    return res?.data?.pwmProfileStrategyItemUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfileStrategyItem(
  id: number
): Promise<PartialNullable<IPwmProfileStrategyItemModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileStrategyItemGetByIdDocument,
      variables: {
        id,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileStrategyItemGetById;
  } catch (error) {
    throw error;
  }
}

export async function deletePwmProfileStrategyItem(
  id: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileStrategyItemDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + own lists
    executeEviction(client, {
      typename: "PwmProfileStrategyItem",
      id: id.toString(),
      fieldNames: [
        "pwmProfileStrategyItemListPaginated",
        "pwmProfileStrategyItemList",
      ],
    });

    // Evict all parent PwmProfileStrategy entities (they reference these items)
    executeEviction(client, {
      typename: "PwmProfileStrategy",
      fieldNames: [
        "pwmProfileStrategyListPaginated",
        "pwmProfileStrategyGetById",
      ],
    });

    return Boolean(res?.data?.pwmProfileStrategyItemDelete);
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfileStrategyItemList(): Promise<
  CommonListModel[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileStrategyItemListDocument,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileStrategyItemList;
  } catch (error) {
    throw error;
  }
}
